import axios from 'axios';
import keycloak from '../services/keycloak/keycloak';
import { updateActivity } from '../utils/sessionManager';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper: check if a JWT access token is expired (with a 10-second buffer)
const isTokenExpired = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const payload = JSON.parse(atob(base64Url.replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp * 1000 < Date.now() + 10000; // 10-second buffer
  } catch {
    return true; // Treat unreadable tokens as expired
  }
};

// Helper: attempt a proactive token refresh before expiry triggers a 401
const proactiveRefresh = async () => {
  const storedTokens =
    sessionStorage.getItem('keycloak_tokens') || localStorage.getItem('keycloak_tokens');
  if (!storedTokens) return null;
  try {
    const parsed = JSON.parse(storedTokens);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    const refreshResp = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: parsed.refresh_token }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!refreshResp.ok) return null;
    const newTokens = await refreshResp.json();
    if (!newTokens.access_token) return null;

    keycloak.token = newTokens.access_token;
    keycloak.refreshToken = newTokens.refresh_token;
    keycloak.authenticated = true;

    const isSession = !!sessionStorage.getItem('keycloak_tokens');
    const storage = isSession ? sessionStorage : localStorage;
    storage.setItem(
      'keycloak_tokens',
      JSON.stringify({
        access_token: newTokens.access_token,
        refresh_token: newTokens.refresh_token,
        id_token: newTokens.id_token,
        expires_at: Date.now() + (newTokens.expires_in || 300) * 1000,
      })
    );
    return newTokens.access_token;
  } catch (e) {
    console.warn('⚠️ Proactive token refresh failed:', e.message);
    return null;
  }
};

// Add Bearer token to all requests
axiosInstance.interceptors.request.use(
  async (config) => {
    updateActivity();

    let token = keycloak?.authenticated && keycloak?.token ? keycloak.token : null;

    // Fall back to storage if in-memory token is missing
    if (!token) {
      const storedTokens =
        sessionStorage.getItem('keycloak_tokens') || localStorage.getItem('keycloak_tokens');
      if (storedTokens) {
        try {
          const parsed = JSON.parse(storedTokens);
          if (parsed.access_token) {
            keycloak.token = parsed.access_token;
            token = parsed.access_token;
          }
        } catch (e) {
          console.warn('⚠️ Could not parse stored tokens');
        }
      }
    }

    // Proactively refresh if the token we have is expired
    if (token && isTokenExpired(token)) {
      console.warn('⚠️ Access token is expired — attempting proactive refresh...');
      const refreshed = await proactiveRefresh();
      token = refreshed || token; // Use refreshed token if available; 401 handler will catch failures
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('⚠️ Axios: No token available');
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle responses
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Silently handle cancelled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    console.error('❌ Axios Response Error:', {
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
    });

    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;
      console.warn('⚠️ Got 401, attempting token refresh before giving up...');

      // Try to refresh token inline
      const storedTokens =
        sessionStorage.getItem('keycloak_tokens') || localStorage.getItem('keycloak_tokens');
      if (storedTokens) {
        try {
          const parsed = JSON.parse(storedTokens);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

          const refreshResp = await fetch(`${API_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: parsed.refresh_token }),
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (refreshResp.ok) {
            const newTokens = await refreshResp.json();
            if (newTokens.access_token) {
              keycloak.token = newTokens.access_token;
              keycloak.refreshToken = newTokens.refresh_token;
              keycloak.authenticated = true;

              // Update stored tokens
              const isSession = !!sessionStorage.getItem('keycloak_tokens');
              const storage = isSession ? sessionStorage : localStorage;
              storage.setItem(
                'keycloak_tokens',
                JSON.stringify({
                  access_token: newTokens.access_token,
                  refresh_token: newTokens.refresh_token,
                  id_token: newTokens.id_token,
                  expires_at: Date.now() + (newTokens.expires_in || 300) * 1000,
                })
              );

              // Retry original request with new token
              error.config.headers.Authorization = `Bearer ${newTokens.access_token}`;
              return axiosInstance(error.config);
            }
          }
        } catch (refreshErr) {
          console.error('Token refresh during 401 failed:', refreshErr);
        }
      }

      // If refresh fails, redirect to login cleanly
      console.error('❌ Session cannot be recovered. Redirecting to login.');
      localStorage.removeItem('keycloak_tokens');
      sessionStorage.removeItem('keycloak_tokens');
      window.location.href =
        '/login?message=' + encodeURIComponent('Your session has expired. Please login again.');
    }

    return Promise.reject(error);
  }
);

axiosInstance.isCancel = axios.isCancel;
export default axiosInstance;
